import { IAMService, User, UserIdentifier } from '@vendasta/iamv2';
import {
  CrmDependencies,
  CrmObjectDependencies,
  ObjectType,
  SimplifiedUser,
  UserWithDisplayName,
} from './tokens-and-interfaces';
import {
  GalaxyDateDefault,
  GalaxyFilterInterface,
  GalaxyFilterOperator,
  GalaxyFilterType,
  GalaxyFilterValueInterface,
} from '@vendasta/galaxy/filter/chips';
import {
  FilterGroupInterface,
  FilterValueInterface,
} from '@vendasta/crm/lib/_internal/interfaces/galaxy-filters.interface';
import { DateDefault, FilterGroupOperator, FilterInterface, FilterOperator, FilterType } from '@vendasta/crm';
import { Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';

export function userFilterFn(user: UserWithDisplayName, searchTerm: string): boolean {
  searchTerm = searchTerm?.toLowerCase();
  const includesFullName = user?.displayName?.toLowerCase()?.includes(searchTerm);
  const includesFirstName = user?.firstName?.toLowerCase()?.includes(searchTerm);
  const includesLastName = user?.lastName?.toLowerCase()?.includes(searchTerm);
  const includesUserId = user?.userId?.toLowerCase()?.includes(searchTerm);
  const includesEmail = user?.email?.toLowerCase()?.includes(searchTerm);
  return includesFullName || includesFirstName || includesLastName || includesUserId || includesEmail;
}

export function convertToReadableUsers(users: User[] | SimplifiedUser[]): UserWithDisplayName[] {
  return users.map(
    (user) =>
      ({
        ...user,
        displayName: `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() ?? user?.userId,
      }) as UserWithDisplayName,
  );
}

export function convertGalaxyFilters(filters: GalaxyFilterInterface[] | undefined): FilterGroupInterface | undefined {
  if (!filters) {
    return undefined;
  }
  const filterGroup = {} as FilterGroupInterface;
  filterGroup.operator = FilterGroupOperator.FILTER_GROUP_OPERATOR_AND;
  filterGroup.filters = [];
  filters.forEach((inputFilter) => {
    if (!inputFilter.fieldId || !inputFilter.operator) {
      return;
    }
    const filterOperator: FilterOperator = inputFilter.operator
      ? FilterOperator[GalaxyFilterOperator[inputFilter.operator]]
      : undefined;
    const filterGroupFilter: FilterInterface = {
      fieldId: inputFilter.fieldId,
      operator: filterOperator,
      values: [],
      floatTolerance: inputFilter.floatTolerance,
    };
    filterGroupFilter.values = inputFilter.values ? convertFromGalaxyFilterValues(inputFilter.values) : undefined;
    filterGroup.filters?.push(filterGroupFilter);
  });
  return filterGroup;
}

export function convertToGalaxyFilter(filter: FilterInterface): GalaxyFilterInterface {
  return {
    fieldId: filter.fieldId,
    operator: filter.operator
      ? convertToGalaxyFilterOperator(filter.operator)
      : GalaxyFilterOperator.FILTER_OPERATOR_INVALID,
    values: filter.values ? convertToGalaxyFilterValues(filter.values) : [],
    floatTolerance: filter.floatTolerance,
  } as GalaxyFilterInterface;
}

export function convertToGalaxyFilterType(type: FilterType): GalaxyFilterType {
  return GalaxyFilterType[FilterType[type]];
}

export function convertToGalaxyFilterOperator(operator: FilterOperator): GalaxyFilterOperator {
  return GalaxyFilterOperator[FilterOperator[operator]];
}

export function convertToGalaxyFilterOperators(operators: FilterOperator[]): GalaxyFilterOperator[] {
  return operators.map((o) => convertToGalaxyFilterOperator(o));
}

export function convertToGalaxyFilterValues(values: FilterValueInterface[]): GalaxyFilterValueInterface[] {
  return values.map((v) => convertToGalaxyFilterValue(v));
}

export function convertToGalaxyFilterValue(value: FilterValueInterface): GalaxyFilterValueInterface {
  const dateDefault: GalaxyDateDefault = value.dateDefault
    ? GalaxyDateDefault[DateDefault[value.dateDefault]]
    : undefined;
  return {
    boolean: value.boolean,
    integer: value.integer,
    string: value.string,
    date: value.date,
    dateDefault: dateDefault,
    float: value.float,
    phone: value.phone,
  } as GalaxyFilterValueInterface;
}

export function convertFromGalaxyFilterValues(values: GalaxyFilterValueInterface[]): FilterValueInterface[] {
  return values.map((v) => convertFromGalaxyFilterValue(v));
}

export function convertFromGalaxyFilterValue(value: GalaxyFilterValueInterface): FilterValueInterface {
  const dateDefault: DateDefault = value.dateDefault ? DateDefault[GalaxyDateDefault[value.dateDefault]] : undefined;
  return {
    boolean: value.boolean,
    integer: value.integer,
    string: value.string,
    date: value.date,
    dateDefault: dateDefault,
    float: value.float,
    phone: value.phone,
  } as FilterValueInterface;
}

export function getObjectDependencies(objectType: ObjectType, parentConfig: CrmDependencies): CrmObjectDependencies {
  const emptyConfig: CrmObjectDependencies = {};
  switch (objectType) {
    case 'Company':
      return parentConfig.company || emptyConfig;
    case 'Contact':
      return parentConfig.contact || emptyConfig;
    case 'Activity':
      return parentConfig.activity || emptyConfig;
    case 'Opportunity':
      return parentConfig.opportunity || emptyConfig;
    default:
      return emptyConfig;
  }
}

export function getMultiUsers(
  uniqueUserIds: string[],
  config: CrmDependencies,
  iamService: IAMService,
): Observable<SimplifiedUser[] | User[]> {
  if (config.services?.userService?.getMultiUsers) {
    return config.services.userService.getMultiUsers(uniqueUserIds).pipe(
      catchError((error) => {
        console.error(error);
        return of([]);
      }),
    );
  } else if (iamService) {
    const uniqueUserIdentifiers = uniqueUserIds.map((userId) => new UserIdentifier({ userId: userId }));
    return iamService.getMultiUsers(uniqueUserIdentifiers).pipe(
      catchError((error) => {
        console.error(error);
        return of([]);
      }),
    );
  } else {
    throw new Error('Service not defined (CRMUserService | IAMService)');
  }
}
